/**
 * Streak tracking — exact port of mobile services/streak.ts (same StreakData
 * shape on lb_users/{uid}.streakData, same milestone days), so a streak
 * continued on web reads identically on the phone.
 */
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { todayStr, addDays } from '@/lib/dates'
import type { StreakData } from '@/types'

export type { StreakData }

export const STREAK_MILESTONES = [
  { days: 3, emoji: '🔥' },
  { days: 7, emoji: '⭐' },
  { days: 14, emoji: '🌟' },
  { days: 30, emoji: '🏆' },
  { days: 60, emoji: '💎' },
  { days: 100, emoji: '👑' },
] as const

export const DEFAULT_STREAK_DATA: StreakData = {
  currentStreak: 0,
  longestStreak: 0,
  lastLogDate: '',
  streakStartDate: '',
  milestones: [],
  freezeDates: [],
}

/**
 * Streak freezes — exact port of mobile services/streak.ts: 2 auto-applied
 * freezes per calendar month, not banked across months. A gap between the
 * last log and today is bridged (streak continues) if it's fully covered by
 * the freezes remaining this month; otherwise the streak resets as before.
 * `freezeDates` is the append-only ledger used both to enforce the monthly
 * cap and to mark frozen days in the UI.
 */
export const MONTHLY_FREEZE_LIMIT = 2

function isValidDateString(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s)
}

/** The 'YYYY-MM-DD' dates strictly between `lastLogDate` and `today` (exclusive of both). */
function getMissedDates(lastLogDate: string, today: string): string[] {
  const dates: string[] = []
  let cursor = addDays(lastLogDate, 1)
  while (cursor < today) {
    dates.push(cursor)
    cursor = addDays(cursor, 1)
  }
  return dates
}

/**
 * Whether `missedDates` can be fully covered by streak freezes, given
 * `existingFreezeDates` already recorded. Each missed date is charged
 * against its own calendar month's allowance, so a gap that crosses a month
 * boundary (e.g. Jan 31 + Feb 1) draws from each month independently —
 * "today's month" alone is never the sole gate. Freezes used in prior
 * months don't count against a different month's cap (not banked, but also
 * not lost early — a rollover just restores full capacity for that month).
 */
function canCoverGapWithFreezes(missedDates: string[], existingFreezeDates: string[]): boolean {
  if (missedDates.length === 0) return false

  const missedByMonth = new Map<string, number>()
  for (const date of missedDates) {
    const month = date.slice(0, 7) // 'YYYY-MM'
    missedByMonth.set(month, (missedByMonth.get(month) || 0) + 1)
  }

  for (const [month, missedCount] of missedByMonth) {
    const usedInMonth = existingFreezeDates.filter((d) => d.slice(0, 7) === month).length
    if (usedInMonth + missedCount > MONTHLY_FREEZE_LIMIT) {
      return false
    }
  }

  return true
}

/**
 * Every milestone strictly greater than `oldStreak` and at most `newStreak`
 * that hasn't already been recorded. A freeze can bridge more than one
 * missed day, so a single update can cross more than one milestone at once
 * (e.g. currentStreak 5 + 2 frozen days -> 8 crosses the 7-day milestone) —
 * a plain equality check against `newStreak` would silently skip it.
 */
function findCrossedMilestones(
  oldStreak: number,
  newStreak: number,
  recordedMilestones: number[],
): typeof STREAK_MILESTONES[number][] {
  return STREAK_MILESTONES.filter(
    (m) => m.days > oldStreak && m.days <= newStreak && !recordedMilestones.includes(m.days),
  )
}

async function readStreak(uid: string): Promise<StreakData> {
  const snap = await getDoc(doc(db, 'lb_users', uid))
  const data = snap.exists() ? (snap.data().streakData as StreakData | undefined) : undefined
  return data || { ...DEFAULT_STREAK_DATA }
}

async function writeStreak(uid: string, streakData: StreakData): Promise<void> {
  await setDoc(
    doc(db, 'lb_users', uid),
    { streakData, updatedAt: serverTimestamp() },
    { merge: true },
  )
}

export interface StreakUpdateResult {
  streakData: StreakData
  newMilestoneDays: number | null
  streakIncremented: boolean
  // True when this update bridged a logging gap using one or more streak
  // freezes instead of resetting the streak.
  freezeConsumed: boolean
  freezeDatesUsed: string[]
}

/**
 * Call after any diary entry is created — mirrors mobile StreakService.updateStreak.
 * Pass the entry's own date (`entryDate`) so entries placed on a future day —
 * copying today's meals to tomorrow, or logging directly onto a future day —
 * credit that day too; without it the pre-logged day is never marked logged
 * and the streak breaks on the user's next real log.
 */
export async function updateStreak(uid: string, entryDate?: string): Promise<StreakUpdateResult> {
  const today = todayStr()
  const yesterday = addDays(today, -1)
  const streakData = await readStreak(uid)
  const existingFreezeDates = streakData.freezeDates || []

  // null = the act-of-logging pass changed nothing (today already credited,
  // or lastLogDate is already a pre-logged future day).
  let updated: StreakData | null = null
  let newMilestoneDays: number | null = null
  let freezeConsumed = false
  let freezeDatesUsed: string[] = []

  if (streakData.lastLogDate >= today) {
    updated = null
  } else if (streakData.lastLogDate === yesterday) {
    const newStreak = streakData.currentStreak + 1
    const crossedMilestones = findCrossedMilestones(
      streakData.currentStreak,
      newStreak,
      streakData.milestones,
    )
    const milestone = crossedMilestones[crossedMilestones.length - 1] ?? null
    updated = {
      currentStreak: newStreak,
      longestStreak: Math.max(newStreak, streakData.longestStreak),
      lastLogDate: today,
      streakStartDate: streakData.streakStartDate,
      milestones: crossedMilestones.length > 0
        ? [...streakData.milestones, ...crossedMilestones.map((m) => m.days)]
        : streakData.milestones,
      freezeDates: existingFreezeDates,
    }
    newMilestoneDays = milestone?.days ?? null
  } else {
    const lastLogValid = isValidDateString(streakData.lastLogDate)
    const missedDates = lastLogValid && streakData.lastLogDate < yesterday
      ? getMissedDates(streakData.lastLogDate, today)
      : []
    const gapCoverable = lastLogValid && missedDates.length > 0
      && canCoverGapWithFreezes(missedDates, existingFreezeDates)

    if (gapCoverable) {
      // Gap fully covered by freezes — continue the streak exactly as if
      // the user had logged every missed day, including it in the count.
      const newStreak = streakData.currentStreak + missedDates.length + 1
      const crossedMilestones = findCrossedMilestones(
        streakData.currentStreak,
        newStreak,
        streakData.milestones,
      )
      const milestone = crossedMilestones[crossedMilestones.length - 1] ?? null
      updated = {
        currentStreak: newStreak,
        longestStreak: Math.max(newStreak, streakData.longestStreak),
        lastLogDate: today,
        streakStartDate: streakData.streakStartDate,
        milestones: crossedMilestones.length > 0
          ? [...streakData.milestones, ...crossedMilestones.map((m) => m.days)]
          : streakData.milestones,
        freezeDates: [...existingFreezeDates, ...missedDates],
      }
      newMilestoneDays = milestone?.days ?? null
      freezeConsumed = true
      freezeDatesUsed = missedDates
    } else {
      // Gap too large for the freezes left this month (or first-ever log) —
      // start fresh. Freeze history is kept (independent ledger, not tied to
      // streak length).
      updated = {
        currentStreak: 1,
        longestStreak: Math.max(1, streakData.longestStreak),
        lastLogDate: today,
        streakStartDate: today,
        milestones: streakData.milestones,
        freezeDates: existingFreezeDates,
      }
    }
  }

  // Credit the entry's own date when it lies one day beyond the last credited
  // day (copying today's meals to tomorrow, or chained copies extending day
  // by day). A farther future date has uncredited days in between — those
  // haven't happened yet, so it neither extends the chain nor consumes freezes.
  const base = updated ?? streakData
  if (
    entryDate &&
    isValidDateString(entryDate) &&
    entryDate > today &&
    isValidDateString(base.lastLogDate) &&
    entryDate === addDays(base.lastLogDate, 1)
  ) {
    const newStreak = base.currentStreak + 1
    const crossedMilestones = findCrossedMilestones(newStreak - 1, newStreak, base.milestones)

    updated = {
      ...base,
      currentStreak: newStreak,
      longestStreak: Math.max(newStreak, base.longestStreak),
      lastLogDate: entryDate,
      milestones: crossedMilestones.length > 0
        ? [...base.milestones, ...crossedMilestones.map((m) => m.days)]
        : base.milestones,
    }
    newMilestoneDays = crossedMilestones[crossedMilestones.length - 1]?.days ?? newMilestoneDays
  }

  if (!updated) {
    // Nothing changed (already credited)
    return { streakData, newMilestoneDays: null, streakIncremented: false, freezeConsumed: false, freezeDatesUsed: [] }
  }

  await writeStreak(uid, updated)
  return { streakData: updated, newMilestoneDays, streakIncremented: true, freezeConsumed, freezeDatesUsed }
}

/** On app open: zero the current streak if the last log was before yesterday. */
export async function checkStreakStatus(uid: string): Promise<StreakData> {
  const yesterday = addDays(todayStr(), -1)
  const streakData = await readStreak(uid)
  if (!isValidDateString(streakData.lastLogDate)) return streakData
  if (streakData.lastLogDate < yesterday && streakData.currentStreak > 0) {
    const today = todayStr()
    const missedDates = getMissedDates(streakData.lastLogDate, today)

    // Gap still coverable by a freeze if the user logs today — don't
    // pre-emptively break the streak. The freeze itself is only spent by
    // updateStreak() when they actually log; this passive read must not
    // consume a freeze on its own. Uses the same per-month rule as
    // updateStreak() so this check can never disagree with the log-time
    // decision.
    if (canCoverGapWithFreezes(missedDates, streakData.freezeDates || [])) {
      return streakData
    }

    const updated = { ...streakData, currentStreak: 0, freezeDates: streakData.freezeDates || [] }
    await writeStreak(uid, updated)
    return updated
  }
  return streakData
}

/**
 * Refund streak freezes for dates that now have real entries (mobile
 * StreakService.refundFreezesForDates port). Backfilling a missed day means
 * its freeze is no longer needed — remove it from the ledger so the calendar
 * shows the real performance dot and the monthly freeze count is restored.
 * Non-fatal on failure: the freeze stays charged.
 */
export async function refundFreezesForDates(uid: string, dates: string[]): Promise<void> {
  if (dates.length === 0) return
  try {
    const snap = await getDoc(doc(db, 'lb_users', uid))
    const streakData = snap.data()?.streakData as { freezeDates?: string[] } | undefined
    const freezeDates = streakData?.freezeDates
    if (!streakData || !freezeDates?.length) return
    const toRefund = new Set(dates)
    const remaining = freezeDates.filter((d) => !toRefund.has(d))
    if (remaining.length === freezeDates.length) return
    await updateDoc(doc(db, 'lb_users', uid), {
      streakData: { ...streakData, freezeDates: remaining },
    })
  } catch (err) {
    console.warn('Failed to refund streak freeze:', err)
  }
}
